import React, { useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';

// ===== Printable CFD analysis report (SyberJet-branded, ITAR-marked) =====
const RPT = {
  ink: '#1f2937', head: '#0f2b46', sub: '#6b7280', red: '#c8102e',
  border: '#d1d5db', grid: '#e5e7eb', axis: '#6b7280',
};

function fmtNum(v, dec = 4) {
  return (v == null || Number.isNaN(Number(v))) ? '—' : Number(v).toFixed(dec);
}
function objRows(o) {
  return o ? Object.entries(o).map(([k, v]) => [k, String(v)]) : [];
}
export function parseConfig(name) {
  const out = {};
  let m = /M(\d)p(\d+)/.exec(name || '');
  if (m) out.mach = `${m[1]}.${m[2]}`;
  m = /Alpha(\d)p(\d+)/i.exec(name || '');
  if (m) out.alpha = `${m[1]}.${m[2]}°`;
  return out;
}

function RptSectionTitle({ children, n }) {
  return (
    <h2 style={{
      color: RPT.head, fontSize: 12, fontWeight: 700, margin: '16px 0 6px',
      borderBottom: `2px solid ${RPT.red}`, paddingBottom: 3,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{n ? `${n}. ` : ''}{children}</h2>
  );
}
function RptTable({ rows }) {
  if (!rows || rows.length === 0) return <div style={{ fontSize: 10, color: RPT.sub }}>—</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} style={{ borderBottom: `1px solid ${RPT.border}` }}>
            <td style={{ color: RPT.sub, padding: '3px 8px', width: '46%', verticalAlign: 'top' }}>{k}</td>
            <td style={{ color: RPT.ink, padding: '3px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function RptLineChart({ title, data, lines, dualAxis }) {
  if (!data || data.length < 2) return null;
  return (
    <div className="report-block" style={{ marginBottom: 10 }}>
      <div style={{ color: RPT.head, fontSize: 10.5, fontWeight: 700, marginBottom: 3 }}>{title}</div>
      <div style={{ border: `1px solid ${RPT.border}`, borderRadius: 4, padding: 6, background: '#fff' }}>
        <LineChart width={690} height={185} data={data} margin={{ top: 6, right: 14, bottom: 2, left: 2 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={RPT.grid} />
          <XAxis dataKey="x" stroke={RPT.axis} tick={{ fontSize: 9, fill: RPT.axis }} tickFormatter={(t) => Number(t).toLocaleString()} />
          {dualAxis ? [
            <YAxis key="l" yAxisId="l" stroke={lines[0].color} tick={{ fontSize: 9, fill: RPT.axis }} width={48} />,
            <YAxis key="r" yAxisId="r" orientation="right" stroke={lines[1].color} tick={{ fontSize: 9, fill: RPT.axis }} width={48} />,
          ] : <YAxis stroke={RPT.axis} tick={{ fontSize: 9, fill: RPT.axis }} width={52} />}
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {lines.map(l => (
            <Line key={l.key} yAxisId={dualAxis ? l.axis : undefined} type="monotone"
              dataKey={l.key} name={l.name} stroke={l.color} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          ))}
        </LineChart>
      </div>
    </div>
  );
}
function RptBar({ breakdown }) {
  const rows = (breakdown || []).slice(0, 12);
  if (rows.length === 0) return null;
  return (
    <div className="report-block" style={{ marginBottom: 10 }}>
      <div style={{ border: `1px solid ${RPT.border}`, borderRadius: 4, padding: 6, background: '#fff' }}>
        <BarChart width={690} height={rows.length * 28 + 40} data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={RPT.grid} horizontal={false} />
          <XAxis type="number" stroke={RPT.axis} tick={{ fontSize: 9, fill: RPT.axis }} tickFormatter={(v) => v.toFixed(3)} />
          <YAxis type="category" dataKey="surface" stroke={RPT.axis} tick={{ fontSize: 9, fill: RPT.ink }} width={100} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Bar dataKey="drag" name="Drag (Fx)" fill="#ea580c" />
          <Bar dataKey="lift" name="Lift (Fz)" fill="#0284c7" />
        </BarChart>
      </div>
    </div>
  );
}

export function JobReport({ data, clusterDef }) {
  const d = data || {};
  const det = d.details || {};
  const cfg = parseConfig(d.jobName);
  const fh = det.forceHistory || {};

  const convData = useMemo(() => {
    const c = det.convergence || {};
    const m = new Map();
    (c.lift || []).forEach(([t, v]) => m.set(t, { x: t, lift: v }));
    (c.drag || []).forEach(([t, v]) => m.set(t, { ...(m.get(t) || { x: t }), drag: v }));
    return Array.from(m.values()).sort((a, b) => a.x - b.x);
  }, [det.convergence]);
  const ldData = useMemo(() => convData
    .filter(r => r.lift != null && r.drag != null && Math.abs(r.drag) > 1e-9)
    .map(r => ({ x: r.x, ld: r.lift / r.drag })), [convData]);
  const single = (arr) => (arr || []).map(([t, v]) => ({ x: t, v }));
  const last = (arr) => (arr && arr.length) ? arr[arr.length - 1][1] : null;
  const lastConv = convData[convData.length - 1] || {};
  const latestLD = ldData.length ? ldData[ldData.length - 1].ld : null;
  const cs = det.convergenceStatus || {};
  const pp = det.pointProbes || {};
  const ppKeys = Object.keys(pp.labels || {});
  const RPT_PROBE = ['#0284c7', '#ea580c', '#7c3aed', '#059669', '#db2777'];
  const ppLines = ppKeys.map((k, i) => ({ key: k, name: pp.labels[k], color: RPT_PROBE[i % RPT_PROBE.length] }));
  const ppToX = (rows) => (rows || []).map(r => ({ ...r, x: r.ts }));

  return (
    <div style={{ background: '#fff', color: RPT.ink, fontFamily: 'Arial, Helvetica, sans-serif', padding: '2px' }}>
      {/* Masthead */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `3px solid ${RPT.red}`, paddingBottom: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: RPT.head, letterSpacing: 1 }}>SYBER<span style={{ color: RPT.red }}>JET</span></div>
          <div style={{ fontSize: 9, color: RPT.sub, letterSpacing: 3 }}>AIRCRAFT</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: RPT.head }}>PowerFLOW&reg; CFD Analysis Report</div>
          <div style={{ fontSize: 9.5, color: RPT.sub }}>Generated {new Date().toLocaleString()}</div>
        </div>
      </div>
      <div style={{ border: `1.5px solid ${RPT.red}`, color: RPT.red, textAlign: 'center', fontSize: 9.5, fontWeight: 700, padding: '3px', borderRadius: 3, marginBottom: 12, letterSpacing: 0.5 }}>
        CONTROLLED TECHNICAL DATA &middot; ITAR (22 CFR 120&ndash;130) &middot; NOT FOR RELEASE TO FOREIGN PERSONS
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: RPT.head }}>{d.jobName}</div>
      <div style={{ fontSize: 9.5, color: RPT.sub, marginBottom: 4, wordBreak: 'break-all' }}>{d.jobDirectory || d.path}</div>

      <div className="report-block">
        <RptSectionTitle n={1}>Job Identification</RptSectionTitle>
        <RptTable rows={[
          ['Configuration', `Mach ${cfg.mach || '—'}, α ${cfg.alpha || '—'}`],
          ['Cluster / Node', `${clusterDef?.label || '—'} / ${d.node || '—'}`],
          ['Status', d.status || '—'],
          ['Overall progress', `${(d.overallProgress ?? 0).toFixed(1)}%`],
          ['Current timestep', (det.progress || {})['Current Timestep'] || '—'],
          ['PowerFLOW version', (det.runtime || {})['PowerFLOW Version'] || '—'],
        ]} />
      </div>

      <div className="report-block">
        <RptSectionTitle n={2}>Key Aerodynamic Results (current)</RptSectionTitle>
        <RptTable rows={[
          ['Lift — Surface Z-Force', fmtNum(lastConv.lift)],
          ['Drag — Surface X-Force', fmtNum(lastConv.drag)],
          ['Lift / Drag ratio (L/D)', latestLD != null ? latestLD.toFixed(2) : '—'],
          ['Convergence (L/D)', cs.settled == null ? '—'
            : cs.settled ? `Settled${cs.settledAtTimestep ? ` since timestep ${Number(cs.settledAtTimestep).toLocaleString()}` : ''}`
              : `Converging — ${cs.spreadPct}% spread`],
          ['Side force (Fy)', fmtNum(last(fh.sideForce), 5)],
          ['Pitching moment (My)', fmtNum(last(fh.pitchMoment), 2)],
          ['Rolling moment (Mx)', fmtNum(last(fh.rollMoment), 2)],
          ['Yawing moment (Mz)', fmtNum(last(fh.yawMoment), 2)],
        ]} />
        <div style={{ fontSize: 9, color: RPT.sub, marginTop: 4, fontStyle: 'italic' }}>
          Values are PowerFLOW native surface measurements (lattice units, per-area normalized).
          Conversion to non-dimensional Cl/Cd/Cm requires the case reference area and freestream
          dynamic pressure and is not derived here.
        </div>
      </div>

      <div className="report-block">
        <RptSectionTitle n={3}>Case &amp; Solver Configuration</RptSectionTitle>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ flex: 1 }}><RptTable rows={objRows(det.case)} /></div>
          <div style={{ flex: 1 }}><RptTable rows={[...objRows(det.solver), ...objRows(det.decomp), ...objRows(det.system)]} /></div>
        </div>
      </div>

      <RptSectionTitle n={4}>Convergence Analysis</RptSectionTitle>
      <RptLineChart title="Force convergence — lift &amp; drag vs timestep" data={convData} dualAxis
        lines={[{ key: 'lift', name: 'Lift (Fz)', color: '#0284c7', axis: 'l' }, { key: 'drag', name: 'Drag (Fx)', color: '#ea580c', axis: 'r' }]} />
      <RptLineChart title={`Lift / drag ratio — efficiency (current L/D ${latestLD != null ? latestLD.toFixed(2) : '—'})`} data={ldData}
        lines={[{ key: 'ld', name: 'L/D', color: '#7c3aed' }]} />
      <RptLineChart title="Pitching moment (My) vs timestep" data={single(fh.pitchMoment)} lines={[{ key: 'v', name: 'My', color: '#db2777' }]} />
      <RptLineChart title="Rolling moment (Mx) vs timestep" data={single(fh.rollMoment)} lines={[{ key: 'v', name: 'Mx', color: '#ca8a04' }]} />
      <RptLineChart title="Yawing moment (Mz) vs timestep" data={single(fh.yawMoment)} lines={[{ key: 'v', name: 'Mz', color: '#0284c7' }]} />
      <RptLineChart title="Side force (Fy) vs timestep" data={single(fh.sideForce)} lines={[{ key: 'v', name: 'Fy', color: '#059669' }]} />

      <RptSectionTitle n={5}>Aerodynamic Load Breakdown by Surface</RptSectionTitle>
      <RptBar breakdown={det.forceBreakdown} />
      {Array.isArray(det.forceBreakdown) && det.forceBreakdown.length > 0 && (
        <div className="report-block"><RptTable rows={det.forceBreakdown.map(r => [r.surface, `drag ${r.drag}    lift ${r.lift}`])} /></div>
      )}

      {ppKeys.length > 0 && (pp.pressure || []).length > 1 && (
        <>
          <RptSectionTitle n={6}>Point-Probe Flow Data</RptSectionTitle>
          <RptLineChart title="Static pressure at probe points vs timestep" data={ppToX(pp.pressure)} lines={ppLines} />
          <RptLineChart title="Velocity magnitude at probe points vs timestep" data={ppToX(pp.velocity)} lines={ppLines} />
        </>
      )}

      {((det.warnings_summary && Object.keys(det.warnings_summary).length > 0) || det.errorCount > 0) && (
        <div className="report-block">
          <RptSectionTitle n={7}>Solver Warnings &amp; Notes</RptSectionTitle>
          <RptTable rows={[...objRows(det.warnings_summary),
            ...(det.errorCount ? [['Solver errors', Number(det.errorCount).toLocaleString()]] : [])]} />
          {det.errorCount > 0 && (
            <div style={{ fontSize: 9.5, color: RPT.sub, marginTop: 6 }}>
              Note: &ldquo;velocity exceeds dynamic range&rdquo; errors indicate local flow exceeding the case&rsquo;s
              configured dynamic range (common at transonic Mach). Review the maximum-expected-velocity setting
              before using these results for design decisions.
            </div>
          )}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${RPT.border}`, marginTop: 16, paddingTop: 6, fontSize: 8.5, color: RPT.sub, display: 'flex', justifyContent: 'space-between' }}>
        <span>SyberJet Aircraft — PowerFLOW CFD Analysis Report</span>
        <span>CONTROLLED TECHNICAL DATA (ITAR) — authorized in-scope systems only</span>
      </div>
    </div>
  );
}
